import type {
  CashShiftRecord,
  CoachConfigurationImportResult,
  CoachConfigurationSnapshot,
  CoachMonthlyCommissionSummary,
  CoachProfile,
  CoachProfileAccountOnboardingRequest,
  CoachProfileAccountOnboardingResult,
  CoachQueueActionRequest,
  CoachRuntimeSnapshot,
  CoachServiceRecord,
  CoachServiceReplacementResult,
  CoachServiceRule,
  CoachShift,
  CoachSummaryRow,
  CoachWorkbench,
  CreateMemberRequest,
  GatewayStatus,
  GroupBuyChannelRecord,
  GroupBuyChannelUpsertRequest,
  GroupBuyFulfillmentRecord,
  GroupBuyPackageRecord,
  GroupBuyPackageUpsertRequest,
  GroupBuyReconciliationSummary,
  InventoryMovementRecord,
  InventoryOperationRequest,
  InventoryProductRecord,
  ManualReviewItem,
  MemberAdjustmentRequest,
  MemberDashboard,
  MemberFundTransactionRecord,
  MemberRechargeRecord,
  MemberRechargeRequest,
  MemberRecord,
  MemberRefundRequest,
  MemberRefundStatusResult,
  MemberSpendResult,
  OperatingConfigurationApplyRequest,
  OperatingConfigurationApplyResult,
  OperatingConfigurationBundle,
  OperatingConfigurationPreviewRequest,
  OperatingConfigurationPreviewResult,
  OrderDetail,
  PaymentMethod,
  ProductCatalogImportResult,
  ProductSnapshot,
  ProductUpsertRequest,
  RechargePlanRecord,
  RechargePlanUpsertRequest,
  ReconcileGroupBuyFulfillmentRequest,
  ReconcileMemberRechargeRequest,
  RecordGroupBuyFulfillmentRequest,
  ServiceRequestRecord,
  SystemStatus,
  TableKioskManagementRecord,
  TableSnapshot,
  UpdateMemberRequest,
  VersionInfo,
  WalletDefinitionRecord,
  WalletDefinitionUpsertRequest,
  WalletDisplayGroupRecord,
  WalletDisplayGroupUpsertRequest,
  WalletPaymentRuleRecord,
  WalletPaymentRuleUpsertRequest,
  WalletPaymentTargetOption,
} from "@billiard/contracts";
import {
  markNativeClientUpdateRequired,
  nativeAppInfo,
  nativeClientVersionHeader,
  nativeClientWritesBlocked,
} from "./nativeRuntime";
import { isNativeOperatorApp, type NativeSessionMode, nativeSessionVault } from "./nativeSession";

const gatewayUrl = import.meta.env.VITE_GATEWAY_URL ?? "http://127.0.0.1:4100";
const supportedDemoRoles = new Set(["CASHIER", "MANAGER", "OWNER", "WAITER", "COACH"]);
const demoQuery =
  typeof window === "undefined" ? null : new URLSearchParams(window.location.search);
const requestedDemoRole = demoQuery?.get("demoRole")?.toUpperCase() ?? null;
let operatorHeaders = {
  "content-type": "application/json",
  "x-demo-role":
    requestedDemoRole && supportedDemoRoles.has(requestedDemoRole) ? requestedDemoRole : "CASHIER",
  "x-actor-id": demoQuery?.get("demoActor") ?? "employee_cashier",
};
export interface OperatorIdentity {
  id: string;
  tenantId: string;
  storeId: string;
  login: string;
  displayName: string;
  role: "CASHIER" | "MANAGER" | "OWNER" | "WAITER" | "COACH";
  coachProfileId: string | null;
  expiresAt: string | null;
}

export interface OperatorSessionContext {
  mode: NativeSessionMode;
  shiftId: string | null;
  shiftStatus: "OPEN" | "HANDOVER_PENDING" | null;
}

export function setOperatorRequestIdentity(operator: OperatorIdentity): void {
  operatorHeaders = {
    "content-type": "application/json",
    "x-demo-role": operator.role,
    "x-actor-id": operator.id,
  };
}

function csrfToken(): string | null {
  const native = nativeSessionVault.current();
  if (native) return native.csrfToken;
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [name, ...value] = part.trim().split("=");
    if (name === "billiard_csrf") return decodeURIComponent(value.join("="));
  }
  return null;
}

function requestHeaders(init: RequestInit): Record<string, string> {
  const headers: Record<string, string> = {
    ...operatorHeaders,
    ...(init.headers as Record<string, string> | undefined),
  };
  const native = nativeSessionVault.current();
  const nativeVersion = nativeClientVersionHeader();
  if (isNativeOperatorApp() && nativeVersion) {
    headers["x-billiard-app-version"] = nativeVersion;
  }
  if (native) headers.authorization = `Bearer ${native.accessToken}`;
  if (init.method && !["GET", "HEAD", "OPTIONS"].includes(init.method)) {
    const token = csrfToken();
    if (token) headers["x-csrf-token"] = token;
  }
  return headers;
}
export interface CheckoutResult {
  order: OrderDetail;
  table: TableSnapshot;
  receipt: { effectId: string; status: "QUEUED"; evidence: "MOCK" | "REAL_HARDWARE" };
}

export interface LongSessionResolutionResult {
  order: OrderDetail;
  table: TableSnapshot;
  review: ManualReviewItem;
}

export interface DeviceEffectRecord {
  id: string;
  effectType: "LIGHT_ON" | "LIGHT_OFF" | "PRINT_RECEIPT" | "REPRINT_RECEIPT";
  objectId: string;
  idempotencyKey: string;
  state: "PENDING" | "SUCCEEDED" | "FAILED" | "UNKNOWN";
  evidence: "MOCK" | "REAL_HARDWARE";
  attempts: number;
  detail: Record<string, unknown>;
  result: Record<string, unknown> | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeviceAdapterStatus {
  capabilities: Array<{
    adapterId: string;
    kind: "LIGHT" | "PRINTER";
    displayName: string;
    available: boolean;
    supportsLocalOffline: boolean;
    evidence: "MOCK" | "REAL_HARDWARE";
    commands: string[];
  }>;
  health: Array<{
    status: "HEALTHY" | "DEGRADED" | "UNAVAILABLE";
    checkedAt: string;
    evidence: "MOCK" | "REAL_HARDWARE";
    message: string;
  }>;
  attention: DeviceEffectRecord[];
}

export interface ReconciliationSummary {
  businessDate: string;
  orders: number;
  offlineOrders: number;
  originalAmountFen: number;
  adjustmentsFen: number;
  adjustmentRecords: number;
  receivableFen: number;
  settledFen: number;
  cashFen: number;
  staticWechatFen: number;
  staticAlipayFen: number;
  pendingReconciliationFen: number;
  exceptionReconciliationFen: number;
  unsettledOrders: number;
  unsettledFen: number;
  unexplainedDifferenceFen: number;
  refundedFen: number;
  cashRefundedFen: number;
  staticWechatRefundedFen: number;
  staticAlipayRefundedFen: number;
  conflicts: number;
  evidence: "SIMULATED";
}

export interface OwnerBusinessSummary {
  businessDate: string;
  productRevenueFen: number;
  productCostFen: number;
  productGrossProfitFen: number;
  productQuantity: number;
  productCostKnownQuantity: number;
  missingCostSnapshots: Array<{
    orderId: string;
    tableNumber: string;
    skuId: string;
    productName: string;
    quantity: number;
    unitPriceFen: number;
    startedAt: string;
  }>;
  commissionPayableFen: number;
  refundLossFen: number;
  lowStockProducts: number;
  outOfStockProducts: number;
  expectedCashFen: number;
  countedCashFen: number | null;
  cashDifferenceFen: number | null;
  evidence: "SIMULATED";
}

export class ApiRequestError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const changingRequest = Boolean(init.method && !["GET", "HEAD", "OPTIONS"].includes(init.method));
  const safeUpdateRoute =
    path === "/v1/auth/mobile/login" ||
    path === "/v1/auth/mobile/activate" ||
    path === "/v1/auth/logout";
  if (isNativeOperatorApp() && changingRequest && !safeUpdateRoute && nativeClientWritesBlocked()) {
    throw new ApiRequestError(
      "NATIVE_UPDATE_REQUIRED",
      "当前 App 版本不能安全提交新的经营操作，请先升级；仍可查看记录或退出账号",
    );
  }
  if (
    isNativeOperatorApp() &&
    changingRequest &&
    !safeUpdateRoute &&
    nativeSessionSafetyUnavailable()
  ) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "手机安全存储状态无法确认，已停止新的经营操作；请完全关闭并重新打开 App",
    );
  }
  const nativeAccessTokenAtDispatch = nativeSessionVault.current()?.accessToken ?? null;
  let response: Response;
  try {
    response = await fetch(`${gatewayUrl}${path}`, {
      ...init,
      credentials: isNativeOperatorApp() ? "omit" : "include",
      signal: init.signal ?? AbortSignal.timeout(5_000),
      headers: requestHeaders(init),
    });
  } catch {
    throw new ApiRequestError("GATEWAY_UNREACHABLE", "门店网关连接失败");
  }
  const body = (await response.json()) as T & { code?: string; message?: string };
  if (!response.ok) {
    if (response.status === 426 && isNativeOperatorApp()) {
      markNativeClientUpdateRequired(
        body.code === "NATIVE_SERVER_UPDATE_REQUIRED"
          ? "SERVER_UPDATE_REQUIRED"
          : "UPDATE_REQUIRED",
      );
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("billiard-native-version-refresh"));
      }
    }
    if (
      response.status === 401 &&
      body.code === "AUTH_REQUIRED" &&
      isNativeOperatorApp() &&
      nativeAccessTokenAtDispatch
    ) {
      // Authentication state is authoritative. A failing or hung native bridge
      // must not replace AUTH_REQUIRED with an unrelated storage error. The
      // token guard prevents a late response for A from clearing newer B.
      await clearNativeSessionIfCurrentWithinDeadline(nativeAccessTokenAtDispatch);
    }
    throw new ApiRequestError(body.code ?? "REQUEST_FAILED", body.message ?? "请求失败");
  }
  return body;
}

async function requestText(path: string, init: RequestInit = {}): Promise<string> {
  const changingRequest = Boolean(init.method && !["GET", "HEAD", "OPTIONS"].includes(init.method));
  if (isNativeOperatorApp() && changingRequest && nativeClientWritesBlocked()) {
    throw new ApiRequestError(
      "NATIVE_UPDATE_REQUIRED",
      "当前 App 版本不能安全提交新的经营操作，请先升级；仍可查看记录或退出账号",
    );
  }
  if (isNativeOperatorApp() && changingRequest && nativeSessionSafetyUnavailable()) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "手机安全存储状态无法确认，已停止新的经营操作；请完全关闭并重新打开 App",
    );
  }
  const nativeAccessTokenAtDispatch = nativeSessionVault.current()?.accessToken ?? null;
  let response: Response;
  try {
    response = await fetch(`${gatewayUrl}${path}`, {
      ...init,
      credentials: isNativeOperatorApp() ? "omit" : "include",
      signal: init.signal ?? AbortSignal.timeout(5_000),
      headers: requestHeaders(init),
    });
  } catch {
    throw new ApiRequestError("GATEWAY_UNREACHABLE", "门店网关连接失败");
  }
  const body = await response.text();
  if (!response.ok) {
    let error: { code?: string; message?: string } = {};
    try {
      error = JSON.parse(body) as { code?: string; message?: string };
    } catch {
      // Preserve the stable fallback below for non-JSON proxy errors.
    }
    if (
      response.status === 401 &&
      error.code === "AUTH_REQUIRED" &&
      isNativeOperatorApp() &&
      nativeAccessTokenAtDispatch
    ) {
      await clearNativeSessionIfCurrentWithinDeadline(nativeAccessTokenAtDispatch);
    }
    if (response.status === 426 && isNativeOperatorApp()) {
      markNativeClientUpdateRequired(
        error.code === "NATIVE_SERVER_UPDATE_REQUIRED"
          ? "SERVER_UPDATE_REQUIRED"
          : "UPDATE_REQUIRED",
      );
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("billiard-native-version-refresh"));
      }
    }
    throw new ApiRequestError(error.code ?? "REQUEST_FAILED", error.message ?? "请求失败");
  }
  return body;
}

export function getOperatorSession(): Promise<{
  operator: OperatorIdentity;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
  session?: OperatorSessionContext;
}> {
  return request("/v1/auth/session");
}

const NATIVE_SESSION_CLEANUP_TIMEOUT_MS = 500;
const NATIVE_APP_INFO_TIMEOUT_MS = 500;
let nativeAuthenticationQueue: Promise<void> = Promise.resolve();
let nativeSessionBridgeUnsafe = false;
let nativeAppInfoRequest: ReturnType<typeof nativeAppInfo> | null = null;
let nativeAppInfoTimedOut = false;
const nativeSessionSafetyListeners = new Set<(unsafe: boolean) => void>();
let pendingNativeRevocation: {
  accessToken: string;
  csrfToken: string;
  expiresAt: string;
  appVersion: string;
} | null = null;

/** @internal Test-only process restart simulation; unavailable in release builds. */
export function resetNativeAuthenticationStateForTests(): void {
  if (import.meta.env.MODE !== "test") {
    throw new Error("native authentication state reset is test-only");
  }
  nativeAuthenticationQueue = Promise.resolve();
  setNativeSessionBridgeUnsafe(false);
  nativeAppInfoRequest = null;
  nativeAppInfoTimedOut = false;
  pendingNativeRevocation = null;
  nativeSessionVault.resetProcessMemoryForTests();
}

export async function initializeNativeAuthenticationState(): Promise<void> {
  if (!isNativeOperatorApp()) return;
  const restored = await runNativeStorageOperationWithinDeadline(async () => {
    await nativeSessionVault.restore();
  });
  if (!restored) return;
  await reconcilePendingNativeSessionRevocation().catch(() => undefined);
}

function setNativeSessionBridgeUnsafe(unsafe: boolean): void {
  if (nativeSessionBridgeUnsafe === unsafe) return;
  nativeSessionBridgeUnsafe = unsafe;
  for (const listener of nativeSessionSafetyListeners) listener(unsafe);
}

export function nativeSessionSafetyUnavailable(): boolean {
  return nativeSessionBridgeUnsafe;
}

export function subscribeNativeSessionSafety(listener: (unsafe: boolean) => void): () => void {
  nativeSessionSafetyListeners.add(listener);
  listener(nativeSessionBridgeUnsafe);
  return () => nativeSessionSafetyListeners.delete(listener);
}

async function clearNativeSessionWithinDeadline(): Promise<boolean> {
  return runNativeStorageOperationWithinDeadline(() => nativeSessionVault.clear());
}

async function clearNativeSessionIfCurrentWithinDeadline(accessToken: string): Promise<boolean> {
  return runNativeStorageOperationWithinDeadline(() =>
    nativeSessionVault.clearIfCurrent(accessToken),
  );
}

async function clearPendingNativeRevocationWithinDeadline(): Promise<boolean> {
  return runNativeStorageOperationWithinDeadline(() => nativeSessionVault.clearPendingRevocation());
}

async function savePendingNativeRevocationWithinDeadline(
  pending: NonNullable<typeof pendingNativeRevocation>,
): Promise<boolean> {
  return runNativeStorageOperationWithinDeadline(async () => {
    await nativeSessionVault.savePendingRevocation(pending);
  });
}

async function saveNativeSessionWithinDeadline(
  session: Parameters<typeof nativeSessionVault.save>[0],
): Promise<boolean> {
  return runNativeStorageOperationWithinDeadline(async () => {
    await nativeSessionVault.save(session);
  });
}

async function nativeAppInfoWithinDeadline(): Promise<Awaited<ReturnType<typeof nativeAppInfo>>> {
  if (nativeAppInfoTimedOut) return null;
  // App metadata is immutable for the lifetime of one native process. Reuse
  // one bridge request so an unresponsive Capacitor callback cannot accumulate
  // another permanently pending request on every login retry.
  if (!nativeAppInfoRequest) {
    const request = nativeAppInfo();
    nativeAppInfoRequest = request;
    void request.catch(() => {
      // An explicit native rejection is complete and can be retried. Only a
      // request whose callback never returns is quarantined until App restart.
      if (!nativeAppInfoTimedOut && nativeAppInfoRequest === request) {
        nativeAppInfoRequest = null;
      }
    });
  }
  const appInfoRequest = nativeAppInfoRequest;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  try {
    return await Promise.race([
      appInfoRequest.catch(() => null),
      new Promise<null>((resolve) => {
        timeout = setTimeout(() => {
          timedOut = true;
          nativeAppInfoTimedOut = true;
          resolve(null);
        }, NATIVE_APP_INFO_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
    if (timedOut) nativeAppInfoTimedOut = true;
  }
}

async function runNativeStorageOperationWithinDeadline(
  operation: () => Promise<void>,
): Promise<boolean> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  try {
    const cleared = await Promise.race([
      operation().then(
        () => true,
        () => false,
      ),
      new Promise<boolean>((resolve) => {
        timeout = setTimeout(() => {
          timedOut = true;
          resolve(false);
        }, NATIVE_SESSION_CLEANUP_TIMEOUT_MS);
      }),
    ]);
    // A timed-out remove cannot be cancelled. It may still complete later and
    // delete a newly saved token, so this App process must fail closed until a
    // full restart creates a fresh native bridge instance.
    if (timedOut || !cleared) {
      nativeSessionVault.invalidatePendingOperations();
      setNativeSessionBridgeUnsafe(true);
    }
    return cleared;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

async function revokeNativeSession(
  accessToken: string,
  csrfToken: string,
  appVersion: string,
): Promise<boolean> {
  try {
    const response = await fetch(`${gatewayUrl}/v1/auth/logout`, {
      method: "POST",
      // Native bearer compensation must never inherit a stale browser cookie.
      // Cookie + Bearer is deliberately rejected before the Gateway revokes.
      credentials: "omit",
      signal: AbortSignal.timeout(5_000),
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${accessToken}`,
        "x-csrf-token": csrfToken,
        "x-billiard-app-version": appVersion,
      },
      body: "{}",
    });
    if (response.ok) return true;
    if (response.status !== 401) return false;
    // A retry can receive AUTH_REQUIRED after the first request revoked the
    // token but its response was lost. Other 401 codes, notably credential
    // conflicts, do not prove that this exact bearer token was revoked.
    const body = (await response.json().catch(() => null)) as { code?: unknown } | null;
    return body?.code === "AUTH_REQUIRED";
  } catch {
    return false;
  }
}

export async function reconcilePendingNativeSessionRevocation(): Promise<void> {
  let restoredPending: typeof pendingNativeRevocation = null;
  if (!pendingNativeRevocation && !nativeSessionVault.pendingRevocation()) {
    const restored = await runNativeStorageOperationWithinDeadline(async () => {
      restoredPending = await nativeSessionVault.restorePendingRevocation();
    });
    if (!restored) {
      throw new ApiRequestError(
        "NATIVE_SECURE_SESSION_UNAVAILABLE",
        "手机安全存储状态无法确认；请完全关闭并重新打开 App 后再试",
      );
    }
  }
  const pending =
    pendingNativeRevocation ?? nativeSessionVault.pendingRevocation() ?? restoredPending;
  if (!pending) return;
  pendingNativeRevocation = pending;
  if (!(await revokeNativeSession(pending.accessToken, pending.csrfToken, pending.appVersion))) {
    throw new ApiRequestError(
      "NATIVE_SESSION_REVOCATION_UNKNOWN",
      "仍无法确认上次登录会话是否已撤销；请先恢复门店网络，再重试登录核对",
    );
  }
  // Clear the active token first and the recovery marker last. If active-token
  // cleanup is uncertain, retaining the marker keeps the next restart closed.
  const sessionCleared = await clearNativeSessionWithinDeadline();
  const quarantineCleared = sessionCleared
    ? await clearPendingNativeRevocationWithinDeadline()
    : false;
  if (!sessionCleared || !quarantineCleared) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "上次登录会话已撤销，但手机安全存储仍无法完成清理；请完全关闭并重新打开 App 后再试",
    );
  }
  if (pendingNativeRevocation === pending) pendingNativeRevocation = null;
}

async function loginNativeOperator(
  login: string,
  password: string,
  sessionMode: NativeSessionMode,
): Promise<{ operator: OperatorIdentity }> {
  if (nativeSessionBridgeUnsafe) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "手机安全存储状态无法确认，请完全关闭并重新打开 App 后再登录",
    );
  }
  // Never create another server session while the previous compensation is
  // uncertain. Retrying login first retries revocation with the exact token.
  await reconcilePendingNativeSessionRevocation();
  if (nativeSessionBridgeUnsafe) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "手机安全存储状态无法确认，请完全关闭并重新打开 App 后再登录",
    );
  }
  if (!(await clearNativeSessionWithinDeadline())) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "App 无法安全清理旧登录状态，请重新打开应用并检查手机安全存储后再试",
    );
  }
  const appVersion = (await nativeAppInfoWithinDeadline())?.version;
  if (!appVersion) {
    throw new ApiRequestError(
      "NATIVE_APP_VERSION_REQUIRED",
      "无法读取 App 版本，请重新打开应用后再登录",
    );
  }
  const authenticated = await request<{
    operator: OperatorIdentity;
    session: {
      accessToken: string;
      csrfToken: string;
      expiresAt: string;
      mode: NativeSessionMode;
      shiftId: string | null;
      shiftStatus: "OPEN" | "HANDOVER_PENDING" | null;
      activationState: "PENDING_ACTIVATION" | "ACTIVE";
    };
  }>("/v1/auth/mobile/login", {
    method: "POST",
    body: JSON.stringify({ login, password, sessionMode, appVersion }),
  });
  const pending = {
    accessToken: authenticated.session.accessToken,
    csrfToken: authenticated.session.csrfToken,
    expiresAt: authenticated.session.expiresAt,
    appVersion,
  };
  pendingNativeRevocation = pending;
  let serverActivated = false;
  try {
    if (authenticated.session.activationState !== "PENDING_ACTIVATION") {
      throw new Error("native login did not issue a pending-activation session");
    }
    // Only private-device sessions may persist a revocation-capable token.
    // Public cashier sessions remain memory-only for their entire lifetime.
    if (
      authenticated.session.mode === "PERSONAL" &&
      !(await savePendingNativeRevocationWithinDeadline(pending))
    ) {
      throw new Error("native pending-revocation marker could not be saved");
    }
    if (!(await saveNativeSessionWithinDeadline(authenticated.session))) {
      throw new Error("native session could not be activated");
    }
    const activation = await request<{
      session: {
        expiresAt: string;
        activationState: "ACTIVE";
      };
    }>("/v1/auth/mobile/activate", { method: "POST", body: "{}" });
    if (
      activation.session.activationState !== "ACTIVE" ||
      activation.session.expiresAt !== authenticated.session.expiresAt
    ) {
      throw new Error("native session activation response did not match the issued session");
    }
    serverActivated = true;
    const markerCommitted =
      authenticated.session.mode !== "PERSONAL" ||
      (await clearPendingNativeRevocationWithinDeadline());
    if (!markerCommitted) throw new Error("native activation marker could not be cleared");
    pendingNativeRevocation = null;
  } catch {
    // Start authoritative server revocation before touching the native bridge
    // again. A hung Keychain/Keystore callback must never block that request.
    const revocation = revokeNativeSession(
      pending.accessToken,
      pending.csrfToken,
      pending.appVersion,
    );
    const cleanup = clearNativeSessionWithinDeadline();
    const [revoked, sessionCleared] = await Promise.all([revocation, cleanup]);
    if (revoked && sessionCleared) {
      const quarantineCleared = await clearPendingNativeRevocationWithinDeadline();
      if (quarantineCleared && pendingNativeRevocation === pending) {
        pendingNativeRevocation = null;
      }
    }
    throw new ApiRequestError(
      revoked ? "NATIVE_SECURE_SESSION_UNAVAILABLE" : "NATIVE_SESSION_REVOCATION_UNKNOWN",
      revoked
        ? serverActivated
          ? "App 未能安全确认登录激活，已撤销本次登录；请重新检查手机安全存储后再试"
          : "App 无法安全保存登录状态，已撤销本次登录；请重新检查手机安全存储后再试"
        : "App 无法安全保存登录状态，也无法确认本次会话是否已撤销；请先恢复门店网络，再重新打开 App 登录",
    );
  }
  return { operator: authenticated.operator };
}

export function loginOperator(
  login: string,
  password: string,
  sessionMode: NativeSessionMode = "PERSONAL",
): Promise<{ operator: OperatorIdentity }> {
  if (!isNativeOperatorApp()) {
    return request("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password }),
    });
  }
  // A device has one secure-session slot. Serialize login attempts so an older
  // failure cannot clear or overwrite a newer successful session.
  const attempt = nativeAuthenticationQueue.then(() =>
    loginNativeOperator(login, password, sessionMode),
  );
  nativeAuthenticationQueue = attempt.then(
    () => undefined,
    () => undefined,
  );
  return attempt;
}

async function logoutNativeOperator(): Promise<{ loggedOut: boolean }> {
  // A previous logout can have cleared local memory while its exact server
  // revocation remained unknown. Retry that persisted token before reporting
  // success or inspecting the current active slot.
  await reconcilePendingNativeSessionRevocation();
  const session = nativeSessionVault.current();
  if (!session) return { loggedOut: true };
  const appVersion = (await nativeAppInfoWithinDeadline())?.version ?? nativeClientVersionHeader();
  if (!appVersion) {
    throw new ApiRequestError(
      "NATIVE_APP_VERSION_REQUIRED",
      "无法读取 App 版本，暂不能安全退出；请完全关闭并重新打开 App 后再试",
    );
  }
  const pending = {
    accessToken: session.accessToken,
    csrfToken: session.csrfToken,
    expiresAt: session.expiresAt,
    appVersion,
  };
  pendingNativeRevocation = pending;
  if (session.mode === "PERSONAL" && !(await savePendingNativeRevocationWithinDeadline(pending))) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "手机安全存储无法记录退出状态；请完全关闭并重新打开 App 后再试",
    );
  }
  const [revoked, sessionCleared] = await Promise.all([
    revokeNativeSession(session.accessToken, session.csrfToken, appVersion),
    clearNativeSessionWithinDeadline(),
  ]);
  if (!revoked) {
    throw new ApiRequestError(
      "NATIVE_SESSION_REVOCATION_UNKNOWN",
      "无法确认账号是否已安全退出；请恢复门店网络后重试，系统不会重复创建会话",
    );
  }
  const quarantineCleared =
    session.mode !== "PERSONAL" ||
    (sessionCleared && (await clearPendingNativeRevocationWithinDeadline()));
  if (!sessionCleared || !quarantineCleared) {
    throw new ApiRequestError(
      "NATIVE_SECURE_SESSION_UNAVAILABLE",
      "账号已在服务端退出，但手机安全存储清理未完成；请完全关闭并重新打开 App",
    );
  }
  if (pendingNativeRevocation === pending) pendingNativeRevocation = null;
  return { loggedOut: true };
}

export function logoutOperator(): Promise<{ loggedOut: boolean }> {
  if (!isNativeOperatorApp()) {
    return request("/v1/auth/logout", { method: "POST", body: "{}" });
  }
  const attempt = nativeAuthenticationQueue.then(logoutNativeOperator);
  nativeAuthenticationQueue = attempt.then(
    () => undefined,
    () => undefined,
  );
  return attempt;
}

export function getPublicVersion(): Promise<VersionInfo> {
  return request("/version");
}

export type SecondaryAuthorizationAction =
  | "RETURN_ORDER_ITEM"
  | "REFUND_ORDER"
  | "ADJUST_COACH_SERVICE"
  | "ADJUST_TABLE_SESSION"
  | "MANAGE_OPERATOR_ACCOUNTS"
  | "RESET_OPERATOR_PASSWORD"
  | "ADJUST_MEMBER_BALANCE"
  | "RECONCILE_MEMBER_RECHARGE"
  | "RECONCILE_PAYMENT"
  | "RESOLVE_ORDER_CONFLICT"
  | "RESOLVE_COACH_CONFLICT"
  | "RECONCILE_GROUP_BUY"
  | "MIGRATE_TABLE_NODE"
  | "MANAGE_TABLE_KIOSK"
  | "SETTLE_COACH_COMMISSION_MONTH";

export function requestSecondaryAuthorization(
  action: SecondaryAuthorizationAction,
  credential: string,
  authorizerLogin?: string,
): Promise<{
  token: string;
  authorizerId: string;
  expiresAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}> {
  return request("/v1/auth/secondary-authorizations", {
    method: "POST",
    body: JSON.stringify({ action, credential, authorizerLogin }),
  });
}

function secondaryAuthorizationHeaders(token: string): Record<string, string> {
  return { "x-secondary-authorization": token };
}

export function listTables(): Promise<TableSnapshot[]> {
  return request("/v1/tables");
}

export function getCoachRuntime(businessDate?: string): Promise<CoachRuntimeSnapshot> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return request(`/v1/coaches/runtime${suffix}`);
}

export function getCoachWorkbench(): Promise<CoachWorkbench> {
  return request("/v1/coaches/workbench");
}

export function getCoachConfiguration(): Promise<CoachConfigurationSnapshot> {
  return request("/v1/coaches/configuration");
}

export function listCoachProfilesSensitive(): Promise<CoachProfile[]> {
  return request("/v1/coaches/profiles/sensitive");
}

export function listCoachServiceRules(): Promise<CoachServiceRule[]> {
  return request("/v1/coaches/service-rules");
}

export function checkInCoach(coachId: string, idempotencyKey: string): Promise<CoachShift> {
  return request("/v1/coaches/check-in", {
    method: "POST",
    body: JSON.stringify({ coachId, idempotencyKey }),
  });
}

export function checkOutCoach(
  coachId: string,
  reason: string | null,
  idempotencyKey: string,
): Promise<CoachShift> {
  return request("/v1/coaches/check-out", {
    method: "POST",
    body: JSON.stringify({ coachId, reason, idempotencyKey }),
  });
}

export function changeCoachQueue(input: CoachQueueActionRequest): Promise<CoachShift> {
  return request("/v1/coaches/queue/actions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function assignCoachService(input: {
  sessionId: string;
  coachId: string | null;
  assignmentType: "ROTATION" | "REQUESTED";
  serviceRuleId: string;
  idempotencyKey: string;
}): Promise<CoachServiceRecord> {
  return request("/v1/coaches/services/assign", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function transitionCoachService(
  serviceId: string,
  action: "START" | "COMPLETE" | "END_EARLY" | "CANCEL" | "TERMINATE_ABNORMAL",
  reason: string | null,
  idempotencyKey: string,
): Promise<CoachServiceRecord> {
  return request(`/v1/coaches/services/${serviceId}/transition`, {
    method: "POST",
    body: JSON.stringify({ action, reason, idempotencyKey }),
  });
}

export function replaceCoachService(
  serviceId: string,
  replacementCoachId: string,
  reason: string,
  idempotencyKey: string,
): Promise<CoachServiceReplacementResult> {
  return request(`/v1/coaches/services/${serviceId}/replace`, {
    method: "POST",
    body: JSON.stringify({ replacementCoachId, reason, idempotencyKey }),
  });
}

export function getCoachSummary(businessDate?: string): Promise<CoachSummaryRow[]> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return request(`/v1/coaches/summary${suffix}`);
}

export function getCoachSummaryCsv(businessDate?: string): Promise<string> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return requestText(`/v1/coaches/summary.csv${suffix}`);
}

export function getCoachMonthlyCommission(
  month?: string,
  coachId?: string,
): Promise<CoachMonthlyCommissionSummary[]> {
  const parameters = new URLSearchParams();
  if (month) parameters.set("month", month);
  if (coachId) parameters.set("coachId", coachId);
  const suffix = parameters.size > 0 ? `?${parameters.toString()}` : "";
  return request(`/v1/coaches/monthly-commission${suffix}`);
}

export function settleCoachCommissionMonth(
  input: { coachId: string; month: string; reason: string; idempotencyKey: string },
  authorizationToken: string,
): Promise<CoachMonthlyCommissionSummary> {
  return request("/v1/coaches/monthly-commission/settle", {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function importCoachConfiguration(
  mode: "PREVIEW" | "APPLY",
  configuration: CoachConfigurationSnapshot,
  idempotencyKey: string,
): Promise<CoachConfigurationImportResult> {
  return request("/v1/coaches/configuration/import", {
    method: "POST",
    body: JSON.stringify({ mode, configuration, idempotencyKey }),
  });
}

export function onboardCoachProfileAccount(
  input: CoachProfileAccountOnboardingRequest,
  authorizationToken: string,
): Promise<CoachProfileAccountOnboardingResult> {
  return request("/v1/coaches/profile-account-onboarding", {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export interface OperatorAccountRecord {
  id: string;
  tenantId: string;
  storeId: string;
  login: string;
  displayName: string;
  role: OperatorIdentity["role"];
  coachProfileId: string | null;
  activationStatus: "ACTIVE" | "DISABLED";
  createdAt: string;
  updatedAt: string;
}

export function listOperatorAccounts(): Promise<OperatorAccountRecord[]> {
  return request("/v1/system/operators");
}

export function createOperatorAccount(
  input: {
    login: string;
    displayName: string;
    role: OperatorIdentity["role"];
    coachProfileId: string | null;
    password: string;
    idempotencyKey: string;
  },
  authorizationToken: string,
): Promise<OperatorAccountRecord> {
  return request("/v1/system/operators", {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function setOperatorActivation(
  operatorId: string,
  activationStatus: "ACTIVE" | "DISABLED",
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<OperatorAccountRecord> {
  return request(`/v1/system/operators/${operatorId}/activation`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ activationStatus, reason, idempotencyKey }),
  });
}

export function setOperatorCoachBinding(
  operatorId: string,
  coachProfileId: string | null,
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<OperatorAccountRecord> {
  return request(`/v1/system/operators/${operatorId}/coach-binding`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ coachProfileId, reason, idempotencyKey }),
  });
}

export function resetOperatorPassword(
  operatorId: string,
  password: string,
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<OperatorAccountRecord> {
  return request(`/v1/system/operators/${operatorId}/reset-password`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ password, reason, idempotencyKey }),
  });
}

export type EmployeeRole = Exclude<OperatorIdentity["role"], "COACH">;

export interface EmployeeProfileRecord {
  id: string;
  tenantId: string;
  assignedStoreId: string;
  fullName: string;
  phone: string;
  positionTitle: string;
  notes: string;
  role: EmployeeRole;
  employmentStatus: "EMPLOYED" | "LEFT";
  account: {
    id: string;
    login: string;
    displayName: string;
    role: EmployeeRole;
    activationStatus: "ACTIVE" | "DISABLED";
  } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeProfileAuditRecord {
  id: string;
  actorId: string;
  authorizerId: string | null;
  action: string;
  detail: Record<string, unknown>;
  occurredAt: string;
}

export interface EmployeeProfileFields {
  fullName: string;
  phone: string;
  positionTitle: string;
  notes: string;
  assignedStoreId: string;
  role: EmployeeRole;
  employmentStatus: "EMPLOYED" | "LEFT";
}

export function listEmployeeProfiles(): Promise<EmployeeProfileRecord[]> {
  return request("/v1/staff/profiles");
}

export function createEmployeeProfile(
  input: EmployeeProfileFields & {
    account: { login: string; password: string } | null;
    reason: string;
    idempotencyKey: string;
  },
  authorizationToken: string,
): Promise<EmployeeProfileRecord> {
  return request("/v1/staff/profiles", {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function updateEmployeeProfile(
  employeeId: string,
  input: EmployeeProfileFields & {
    expectedVersion: number;
    reason: string;
    idempotencyKey: string;
  },
  authorizationToken: string,
): Promise<EmployeeProfileRecord> {
  return request(`/v1/staff/profiles/${employeeId}`, {
    method: "PATCH",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function createEmployeeProfileAccount(
  employeeId: string,
  input: { login: string; password: string; reason: string; idempotencyKey: string },
  authorizationToken: string,
): Promise<EmployeeProfileRecord> {
  return request(`/v1/staff/profiles/${employeeId}/account`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function setEmployeeProfileAccountBinding(
  employeeId: string,
  input: { operatorId: string | null; reason: string; idempotencyKey: string },
  authorizationToken: string,
): Promise<EmployeeProfileRecord> {
  return request(`/v1/staff/profiles/${employeeId}/account-binding`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function listEmployeeProfileAudit(
  employeeId: string,
): Promise<EmployeeProfileAuditRecord[]> {
  return request(`/v1/staff/profiles/${employeeId}/audit`);
}

export function getConnectivity(): Promise<GatewayStatus> {
  return request("/v1/connectivity");
}

export function simulateNetwork(offline: boolean): Promise<GatewayStatus> {
  return request("/v1/simulation/network", {
    method: "POST",
    body: JSON.stringify({ offline }),
  });
}

export function listProducts(): Promise<ProductSnapshot[]> {
  return request("/v1/products");
}

export function listInventoryProducts(): Promise<InventoryProductRecord[]> {
  return request("/v1/inventory/products");
}

export function listGroupBuyChannels(includeDisabled = false): Promise<GroupBuyChannelRecord[]> {
  return request(`/v1/group-buy/channels?includeDisabled=${includeDisabled}`);
}

export function saveGroupBuyChannel(
  channelId: string,
  input: GroupBuyChannelUpsertRequest,
): Promise<GroupBuyChannelRecord> {
  return request(`/v1/group-buy/channels/${encodeURIComponent(channelId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function listGroupBuyPackages(includeDisabled = false): Promise<GroupBuyPackageRecord[]> {
  return request(`/v1/group-buy/packages?includeDisabled=${includeDisabled}`);
}

export function saveGroupBuyPackage(
  packageId: string,
  input: GroupBuyPackageUpsertRequest,
): Promise<GroupBuyPackageRecord> {
  return request(`/v1/group-buy/packages/${encodeURIComponent(packageId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function recordGroupBuyFulfillment(
  input: RecordGroupBuyFulfillmentRequest,
): Promise<{ fulfillment: GroupBuyFulfillmentRecord; order: OrderDetail }> {
  return request("/v1/group-buy/fulfillments", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function listGroupBuyFulfillments(
  businessDate?: string,
): Promise<GroupBuyFulfillmentRecord[]> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return request(`/v1/group-buy/fulfillments${suffix}`);
}

export function reconcileGroupBuyFulfillment(
  fulfillmentId: string,
  input: ReconcileGroupBuyFulfillmentRequest,
  authorizationToken: string,
): Promise<{ fulfillment: GroupBuyFulfillmentRecord; order: OrderDetail }> {
  return request(`/v1/group-buy/fulfillments/${encodeURIComponent(fulfillmentId)}/reconcile`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function getGroupBuyReconciliation(
  businessDate?: string,
): Promise<GroupBuyReconciliationSummary> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return request(`/v1/group-buy/reconciliation${suffix}`);
}

export function saveInventoryProduct(input: ProductUpsertRequest): Promise<InventoryProductRecord> {
  return request(`/v1/inventory/products/${encodeURIComponent(input.skuId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function listInventoryMovements(skuId?: string): Promise<InventoryMovementRecord[]> {
  const suffix = skuId ? `?skuId=${encodeURIComponent(skuId)}` : "";
  return request(`/v1/inventory/movements${suffix}`);
}

export function applyInventoryOperation(
  skuId: string,
  input: InventoryOperationRequest,
): Promise<InventoryMovementRecord> {
  return request(`/v1/inventory/products/${encodeURIComponent(skuId)}/operations`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function listMembers(search = ""): Promise<MemberRecord[]> {
  const suffix = search ? `?search=${encodeURIComponent(search)}` : "";
  return request(`/v1/members${suffix}`);
}

export function getMember(memberId: string): Promise<MemberRecord> {
  return request(`/v1/members/${encodeURIComponent(memberId)}`);
}

export function getMemberSensitive(memberId: string): Promise<MemberRecord> {
  return request(`/v1/members/${encodeURIComponent(memberId)}/sensitive`);
}

export function createMember(input: CreateMemberRequest): Promise<MemberRecord> {
  return request("/v1/members", { method: "POST", body: JSON.stringify(input) });
}

export function updateMember(memberId: string, input: UpdateMemberRequest): Promise<MemberRecord> {
  return request(`/v1/members/${encodeURIComponent(memberId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function listWalletDefinitions(includeDisabled = false): Promise<WalletDefinitionRecord[]> {
  return request(`/v1/wallet-definitions?includeDisabled=${includeDisabled}`);
}

export function saveWalletDefinition(
  walletDefinitionId: string,
  input: WalletDefinitionUpsertRequest,
): Promise<WalletDefinitionRecord> {
  return request(`/v1/wallet-definitions/${encodeURIComponent(walletDefinitionId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function listWalletDisplayGroups(
  includeDisabled = false,
): Promise<WalletDisplayGroupRecord[]> {
  return request(`/v1/wallet-display-groups?includeDisabled=${includeDisabled}`);
}

export function saveWalletDisplayGroup(
  groupId: string,
  input: WalletDisplayGroupUpsertRequest,
): Promise<WalletDisplayGroupRecord> {
  return request(`/v1/wallet-display-groups/${encodeURIComponent(groupId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function listWalletPaymentRules(
  includeDisabled = false,
): Promise<WalletPaymentRuleRecord[]> {
  return request(`/v1/wallet-payment-rules?includeDisabled=${includeDisabled}`);
}

export function listWalletPaymentTargets(): Promise<WalletPaymentTargetOption[]> {
  return request("/v1/wallet-payment-targets");
}

export function saveWalletPaymentRule(
  ruleId: string,
  input: WalletPaymentRuleUpsertRequest,
): Promise<WalletPaymentRuleRecord> {
  return request(`/v1/wallet-payment-rules/${encodeURIComponent(ruleId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function listRechargePlans(includeDisabled = false): Promise<RechargePlanRecord[]> {
  return request(`/v1/members/recharge-plans?includeDisabled=${includeDisabled}`);
}

export function saveRechargePlan(
  planId: string,
  input: RechargePlanUpsertRequest,
): Promise<RechargePlanRecord> {
  return request(`/v1/members/recharge-plans/${encodeURIComponent(planId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function rechargeMember(
  memberId: string,
  input: MemberRechargeRequest,
): Promise<{ recharge: MemberRechargeRecord; member: MemberRecord }> {
  return request(`/v1/members/${encodeURIComponent(memberId)}/recharges`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function listMemberRecharges(pendingOnly = false): Promise<MemberRechargeRecord[]> {
  return request(`/v1/member-recharges?pendingOnly=${pendingOnly}`);
}

export function reconcileMemberRecharge(
  rechargeId: string,
  input: ReconcileMemberRechargeRequest,
  authorizationToken: string,
): Promise<{ recharge: MemberRechargeRecord; member: MemberRecord }> {
  return request(`/v1/member-recharges/${encodeURIComponent(rechargeId)}/reconcile`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function refundMemberBalance(
  input: MemberRefundRequest,
  authorizationToken: string,
): Promise<MemberSpendResult> {
  return request<MemberSpendResult>("/v1/member-funds/refund", {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  }).catch((error: unknown) => {
    if (
      error instanceof ApiRequestError &&
      ["GATEWAY_UNREACHABLE", "CLOUD_UNREACHABLE"].includes(error.code)
    ) {
      throw new ApiRequestError(
        "REFUND_RESULT_UNKNOWN",
        "退款请求可能已到达服务端，但页面没有收到最终结果。请按请求号查询，不能盲目重复退款",
      );
    }
    throw error;
  });
}

export function getMemberRefundStatus(
  input: MemberRefundRequest,
): Promise<MemberRefundStatusResult> {
  return request("/v1/member-funds/refund-status", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function adjustMemberBalance(
  memberId: string,
  input: MemberAdjustmentRequest,
  authorizationToken: string,
): Promise<MemberSpendResult> {
  return request(`/v1/members/${encodeURIComponent(memberId)}/adjustments`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify(input),
  });
}

export function listMemberTransactions(memberId: string): Promise<MemberFundTransactionRecord[]> {
  return request(`/v1/members/${encodeURIComponent(memberId)}/transactions`);
}

export function getMemberDashboard(): Promise<MemberDashboard> {
  return request("/v1/members-dashboard");
}

export function openSession(tableId: string, idempotencyKey: string): Promise<TableSnapshot> {
  return request("/v1/sessions/open", {
    method: "POST",
    body: JSON.stringify({ tableId, idempotencyKey }),
  });
}

export function getOrder(sessionId: string): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/order`);
}

export function transferSession(
  sessionId: string,
  targetTableId: string,
  idempotencyKey: string,
  reason = "员工按顾客要求转台",
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/transfer`, {
    method: "POST",
    body: JSON.stringify({ targetTableId, reason, idempotencyKey }),
  });
}

export function adjustSessionStart(
  sessionId: string,
  startedAt: string,
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/adjust-start`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ startedAt, reason, idempotencyKey }),
  });
}

export function pauseSession(
  sessionId: string,
  reason: string,
  idempotencyKey: string,
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/pause`, {
    method: "POST",
    body: JSON.stringify({ reason, idempotencyKey }),
  });
}

export function resumeSession(
  sessionId: string,
  reason: string,
  idempotencyKey: string,
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/resume`, {
    method: "POST",
    body: JSON.stringify({ reason, idempotencyKey }),
  });
}

export function endSessionEarly(
  sessionId: string,
  reason: string,
  idempotencyKey: string,
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/end-early`, {
    method: "POST",
    body: JSON.stringify({ reason, idempotencyKey }),
  });
}

export function resolveLongSession(
  sessionId: string,
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<LongSessionResolutionResult> {
  return request(`/v1/sessions/${sessionId}/resolve-long-session`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ reason, idempotencyKey }),
  });
}

export function voidSession(
  sessionId: string,
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/void`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ reason, idempotencyKey }),
  });
}

export function addOrderItem(
  sessionId: string,
  skuId: string,
  quantity: number,
  idempotencyKey: string,
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/items`, {
    method: "POST",
    body: JSON.stringify({ skuId, quantity, idempotencyKey }),
  });
}

export function returnOrderItem(
  sessionId: string,
  itemId: string,
  quantity: number,
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<OrderDetail> {
  return request(`/v1/sessions/${sessionId}/items/return`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ itemId, quantity, reason, idempotencyKey }),
  });
}

export function checkoutSession(
  sessionId: string,
  method: PaymentMethod,
  idempotencyKey: string,
  member?: { memberId: string; memberBalanceFen?: number },
): Promise<CheckoutResult> {
  return request(`/v1/sessions/${sessionId}/checkout`, {
    method: "POST",
    body: JSON.stringify({ method, idempotencyKey, ...member }),
  });
}

export function clearTable(tableId: string, idempotencyKey: string): Promise<TableSnapshot> {
  return request(`/v1/tables/${tableId}/clear`, {
    method: "POST",
    body: JSON.stringify({ idempotencyKey }),
  });
}

export function listOrders(
  options: {
    offlineOnly?: boolean;
    businessDate?: string;
    dateFrom?: string;
    dateTo?: string;
    orderQuery?: string;
    tableNumber?: string;
    memberSearch?: string;
    paymentMethod?: PaymentMethod | "";
  } = {},
): Promise<OrderDetail[]> {
  const query = new URLSearchParams();
  if (options.offlineOnly) query.set("offlineOnly", "true");
  if (options.businessDate) query.set("businessDate", options.businessDate);
  if (options.dateFrom) query.set("dateFrom", options.dateFrom);
  if (options.dateTo) query.set("dateTo", options.dateTo);
  if (options.orderQuery) query.set("orderQuery", options.orderQuery);
  if (options.tableNumber) query.set("tableNumber", options.tableNumber);
  if (options.memberSearch) query.set("memberSearch", options.memberSearch);
  if (options.paymentMethod) query.set("paymentMethod", options.paymentMethod);
  return request(`/v1/orders${query.size > 0 ? `?${query.toString()}` : ""}`);
}

export function refundOrder(
  orderId: string,
  amountFen: number,
  reason: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<OrderDetail> {
  return request(`/v1/orders/${orderId}/refunds`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ amountFen, reason, idempotencyKey }),
  });
}

export function listManualReview(): Promise<ManualReviewItem[]> {
  return request("/v1/manual-review");
}

export function reconcilePayment(
  reviewId: string,
  outcome: "MANUALLY_RECONCILED" | "NOT_RECEIVED" | "AMOUNT_MISMATCH" | "DUPLICATE_PAYMENT",
  note: string,
  idempotencyKey: string,
  authorizationToken: string,
): Promise<ManualReviewItem> {
  return request(`/v1/manual-review/${reviewId}/reconcile`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ outcome, note, idempotencyKey }),
  });
}

export function resolveOrderConflict(
  reviewId: string,
  note: string,
  idempotencyKey: string,
  disposition: "KEEP_ISOLATED" | "TRANSFER_TO_FINANCE",
  authorizationToken: string,
): Promise<ManualReviewItem> {
  return request(`/v1/manual-review/${reviewId}/resolve-conflict`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ note, idempotencyKey, disposition }),
  });
}

export function resolveCoachConflict(
  reviewId: string,
  note: string,
  idempotencyKey: string,
  disposition: "KEEP_ISOLATED" | "TRANSFER_TO_OPERATIONS",
  authorizationToken: string,
): Promise<ManualReviewItem> {
  return request(`/v1/manual-review/${reviewId}/resolve-coach-conflict`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(authorizationToken),
    body: JSON.stringify({ note, idempotencyKey, disposition }),
  });
}

export function getReconciliationSummary(businessDate?: string): Promise<ReconciliationSummary> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return request(`/v1/reconciliation/summary${suffix}`);
}

const ownerHeaders = { "x-demo-role": "OWNER", "x-actor-id": "owner_demo" };

export interface ProductCatalogPreview {
  rows: Array<{ skuId: string; name: string; unitPriceFen: number; enabled: boolean }>;
  errors: Array<{ line: number; field: string; message: string }>;
  fingerprint: string;
}

export function getSystemStatus(): Promise<SystemStatus> {
  return request("/v1/system/status", { headers: ownerHeaders });
}

export function getOperatingConfiguration(): Promise<OperatingConfigurationBundle> {
  return request("/v1/operating-configuration");
}

export function previewOperatingConfiguration(
  input: OperatingConfigurationPreviewRequest,
): Promise<OperatingConfigurationPreviewResult> {
  return request("/v1/operating-configuration/preview", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function applyOperatingConfiguration(
  input: OperatingConfigurationApplyRequest,
): Promise<OperatingConfigurationApplyResult> {
  return request("/v1/operating-configuration/apply", {
    method: "POST",
    body: JSON.stringify(input),
    // Cloud applies the versioned configuration and regenerates the offline snapshot atomically.
    // Keep the browser deadline longer than the Gateway-to-Cloud 30 second deadline so a slow
    // apply is reported by the server instead of being mislabelled as a disconnected Gateway.
    signal: AbortSignal.timeout(35_000),
  });
}

export function getDeviceAdapterStatus(): Promise<DeviceAdapterStatus> {
  return request("/v1/devices/status");
}

export function getDeviceEffect(effectId: string): Promise<DeviceEffectRecord> {
  return request(`/v1/devices/effects/${encodeURIComponent(effectId)}`);
}

export function listTableKiosks(): Promise<TableKioskManagementRecord[]> {
  return request("/v1/device-management/table-kiosks");
}

export function createKioskPairing(
  tableNodeId: string,
  expectedComponentId: string,
  idempotencyKey: string,
): Promise<{
  tableNodeId: string;
  expectedComponentId: string;
  pairingCode: string;
  expiresAt: string;
  evidence: "STORE_ACCEPTED";
}> {
  return request(
    `/v1/device-management/table-kiosks/${encodeURIComponent(tableNodeId)}/kiosk-pairings`,
    {
      method: "POST",
      body: JSON.stringify({ expectedComponentId, idempotencyKey }),
    },
  );
}

export function revokeKioskBinding(
  tableNodeId: string,
  reason: string,
  idempotencyKey: string,
  secondaryAuthorization: string,
): Promise<TableKioskManagementRecord> {
  return request(`/v1/device-management/table-kiosks/${encodeURIComponent(tableNodeId)}/revoke`, {
    method: "POST",
    headers: secondaryAuthorizationHeaders(secondaryAuthorization),
    body: JSON.stringify({ reason, idempotencyKey }),
  });
}

export function reprintReceipt(
  orderId: string,
  idempotencyKey: string,
): Promise<DeviceEffectRecord> {
  return request(`/v1/orders/${encodeURIComponent(orderId)}/receipt/reprint`, {
    method: "POST",
    body: JSON.stringify({ idempotencyKey }),
  });
}

export function getDiagnosticBundle(): Promise<string> {
  return requestText("/v1/system/diagnostics", { headers: ownerHeaders });
}

export function getOrdersCsv(businessDate?: string): Promise<string> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return requestText(`/v1/system/export/orders.csv${suffix}`, { headers: ownerHeaders });
}

export function getProductCatalogTemplate(): Promise<string> {
  return requestText("/v1/system/import/products/template.csv", { headers: ownerHeaders });
}

export function previewProductCatalog(csv: string): Promise<ProductCatalogPreview> {
  return request("/v1/system/import/products/preview", {
    method: "POST",
    headers: ownerHeaders,
    body: JSON.stringify({ csv }),
  });
}

export function importProductCatalog(
  csv: string,
  idempotencyKey: string,
): Promise<ProductCatalogImportResult> {
  return request("/v1/system/import/products", {
    method: "POST",
    headers: ownerHeaders,
    body: JSON.stringify({ csv, idempotencyKey }),
  });
}

export function getOwnerReconciliationSummary(
  businessDate?: string,
): Promise<ReconciliationSummary> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return request(`/v1/reconciliation/summary${suffix}`, { headers: ownerHeaders });
}

export function getOwnerBusinessSummary(businessDate?: string): Promise<OwnerBusinessSummary> {
  const suffix = businessDate ? `?businessDate=${encodeURIComponent(businessDate)}` : "";
  return request(`/v1/owner/business-summary${suffix}`, { headers: ownerHeaders });
}

export function listCashShifts(): Promise<CashShiftRecord[]> {
  return request("/v1/cash-shifts");
}

export function startCashShift(
  openingCashFen: number,
  note: string,
  idempotencyKey: string,
): Promise<CashShiftRecord> {
  return request("/v1/cash-shifts/start", {
    method: "POST",
    body: JSON.stringify({ openingCashFen, note, idempotencyKey }),
  });
}

export function submitCashShiftHandover(
  shiftId: string,
  countedCashFen: number,
  exceptionNote: string,
  idempotencyKey: string,
): Promise<CashShiftRecord> {
  return request(`/v1/cash-shifts/${encodeURIComponent(shiftId)}/handover`, {
    method: "POST",
    body: JSON.stringify({ countedCashFen, exceptionNote, idempotencyKey }),
  });
}

export function confirmCashShiftHandover(
  shiftId: string,
  note: string,
  idempotencyKey: string,
): Promise<CashShiftRecord> {
  return request(`/v1/cash-shifts/${encodeURIComponent(shiftId)}/receive`, {
    method: "POST",
    body: JSON.stringify({ note, idempotencyKey }),
  });
}

export function listServiceRequests(
  _role: "CASHIER" | "WAITER" = "CASHIER",
): Promise<ServiceRequestRecord[]> {
  return request("/v1/service-requests");
}

export function acknowledgeServiceRequest(
  eventId: string,
  idempotencyKey: string,
): Promise<ServiceRequestRecord> {
  return request(`/v1/service-requests/${eventId}/acknowledge`, {
    method: "POST",
    body: JSON.stringify({ idempotencyKey }),
  });
}

export function completeServiceRequest(
  eventId: string,
  idempotencyKey: string,
): Promise<ServiceRequestRecord> {
  return request(`/v1/service-requests/${eventId}/complete`, {
    method: "POST",
    body: JSON.stringify({ idempotencyKey }),
  });
}
